'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useState } from 'react'
import { alpha } from '@mui/material/styles'
import { Box, Button, Collapse, Divider, Typography } from '@mui/material'

// Types
import type { JobCardProps } from '@/types/components/molecules/careers'

// Components
const JobPostingMolecule = dynamic(() => import('@/components/molecules/careers/job-posting'))
const ArrowDownIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/arrow-down'))

// One position: title, tags, a short line, its status and apply button; the full posting opens right under it
const JobCardMolecule = (props: JobCardProps) => {
	// Props
	const { labels, applyHref, detailsHref, ...job } = props
	const { title, team, type, location, status, description } = job

	// Variables
	const open = status === 'open'
	const hasPosting = job.sections.length > 0
	const [expanded, setExpanded] = useState<boolean>(false)

	return (
		<Box
			sx={{
				height: 1,
				display: 'flex',
				flexDirection: 'column',
				p: { xs: 3, md: 4 },
				borderRadius: 5,
				bgcolor: 'common.white',
				...(!open && { opacity: 0.75 })
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
				<Typography component="h3" sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700 }}>
					{title}
				</Typography>

				<Box
					sx={{
						flexShrink: 0,
						px: 1.5,
						py: 0.5,
						borderRadius: 999,
						fontSize: 12,
						fontWeight: 700,
						color: open ? 'primary.main' : 'text.secondary',
						bgcolor: t => (open ? t.palette.primary.ultralight : alpha(t.palette.grey[500], 0.12))
					}}
				>
					{open ? labels.open : labels.closed}
				</Box>
			</Box>

			<Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
				{[team, type, location].filter(Boolean).map(tag => (
					<Box key={tag} sx={{ px: 1.25, py: 0.25, borderRadius: 1.5, fontSize: 12.5, color: 'text.secondary', bgcolor: 'grey.100' }}>
						{tag}
					</Box>
				))}
			</Box>

			<Typography sx={{ mt: 2, fontSize: 15, lineHeight: 1.9, color: 'text.secondary', flexGrow: 1 }}>{description}</Typography>

			{(open || hasPosting) && (
				<Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
					{open && (
						<Button
							component={Link}
							variant="contained"
							disableElevation
							href={applyHref}
							sx={{ px: 3, py: 1.25, borderRadius: 2, fontSize: 15, fontWeight: 600, textTransform: 'inherit' }}
						>
							{labels.apply}
						</Button>
					)}

					{hasPosting && (
						<Button
							onClick={() => setExpanded(!expanded)}
							aria-expanded={expanded}
							endIcon={<ArrowDownIconAtom />}
							sx={{
								px: 3,
								py: 1.25,
								borderRadius: 2,
								fontSize: 15,
								fontWeight: 600,
								textTransform: 'inherit',
								color: 'text.primary',
								'&:hover': { bgcolor: 'grey.100' },
								'& .MuiButton-endIcon': { mx: 0.75, transition: 'transform .3s', transform: expanded ? 'rotate(180deg)' : 'none' }
							}}
						>
							{expanded ? labels.hideDetails : labels.details}
						</Button>
					)}
				</Box>
			)}

			{hasPosting && (
				<Collapse in={expanded} unmountOnExit={false}>
					<Divider sx={{ my: { xs: 3, md: 4 } }} />

					<Box sx={{ maxWidth: 780 }}>
						<JobPostingMolecule job={job} />
					</Box>

					{detailsHref && (
						<Box component="a" href={detailsHref} sx={{ display: 'inline-block', mt: 4, fontSize: 14, color: 'text.secondary' }}>
							{labels.shareLink}
						</Box>
					)}
				</Collapse>
			)}
		</Box>
	)
}

export default JobCardMolecule
